import type { ReactNode } from 'react';
import { FolderSync, Loader2, MonitorDown } from 'lucide-react';
import {
  ASK_ADMIN_FOR_FOLDER,
  PAIGE_SCAN_DOWNLOAD_LABEL,
  useScanDownload,
  paigeScanSteps,
} from './scan-download';
import { useFolderAgent } from './use-folder-agent';
import './folder-setup.css';

export interface FolderSetupProps {
  /** The job the folder will feed; null outside a Paige job, where nothing can be set up. */
  job: { id: string; name: string } | null;
}

/**
 * No folder feeds the job yet. With Paige Scan on this computer, one button brings its Watched
 * folders window forward with this job picked; the folder is chosen there. Without it, an admin gets
 * the download and the three steps after it; anyone else is told to ask one, because only an admin
 * connects a computer to the company.
 */
export function FolderSetup({ job }: FolderSetupProps): ReactNode {
  const [agent, open] = useFolderAgent(job !== null);
  if (!job) return <p className="paige-scan-checking">This job has no watched folder.</p>;
  if (agent.phase === 'checking')
    return (
      <p className="paige-scan-checking" role="status">
        <Loader2 size={16} className="paige-spin" aria-hidden="true" />
        Looking for Paige Scan on this computer…
      </p>
    );
  if (agent.phase === 'missing')
    return window.paigeWorkbench?.runsCompany === false ? <AskAdmin /> : <Download />;
  return (
    <div className="paige-scan-unavailable" role="status">
      <strong>No folder feeds this job yet</strong>
      <p>Set it up on the computer that can see the folder: this one, or another.</p>
      {agent.opened ? (
        <p>
          Paige Scan opened its Watched folders window: choose the folder, and this job is already
          picked.
        </p>
      ) : (
        <div className="paige-scan-actions">
          <button
            type="button"
            className="paige-scan-button is-primary"
            disabled={agent.busy}
            onClick={() => open(job)}
          >
            <FolderSync size={14} aria-hidden="true" />
            Set up on this computer
          </button>
        </div>
      )}
      {agent.problem && <p className="paige-scan-problem">{agent.problem}</p>}
    </div>
  );
}

/** Paige Scan did not answer here: get it, connect the computer, then the folder is set up in it. */
function Download(): ReactNode {
  const { url } = useScanDownload();
  return (
    <div className="paige-scan-unavailable" role="status">
      <strong>No folder feeds this job yet</strong>
      <p>Paige Scan watches the folder. Install it on the computer that can see the folder.</p>
      <div className="paige-scan-actions">
        <a className="paige-scan-button" href={url}>
          <MonitorDown size={14} aria-hidden="true" />
          {PAIGE_SCAN_DOWNLOAD_LABEL}
        </a>
      </div>
      <ol className="paige-scan-steps">
        {paigeScanSteps('job').map((step) => (
          <li key={step}>{step}</li>
        ))}
      </ol>
    </div>
  );
}

/** Someone who does not run the company cannot connect a computer to it: an admin sets the folder up. */
function AskAdmin(): ReactNode {
  return (
    <div className="paige-scan-unavailable" role="status">
      <strong>No folder feeds this job yet</strong>
      <p>{ASK_ADMIN_FOR_FOLDER}</p>
    </div>
  );
}
